import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import clearProgressTracking from "../../assets/mockups/clear_progress_tracking.png";
import buildInRepitition from "../../assets/mockups/build_in_repitition.png";
import structuredLessons from "../../assets/mockups/structured_lessons.png";
import iphoneMockupFood from "../../assets/mockups/Iphone_mockup_food.png";
import iphoneMockupWine from "../../assets/mockups/Iphone_mockup_wine.png";
import iphoneMockupFood760Webp from "../../assets/mockups/Iphone_mockup_food-760.webp";
import iphoneMockupFood960Webp from "../../assets/mockups/Iphone_mockup_food-960.webp";
import iphoneMockupWine760Webp from "../../assets/mockups/Iphone_mockup_wine-760.webp";
import iphoneMockupWine960Webp from "../../assets/mockups/Iphone_mockup_wine-960.webp";
import onboardingFlow from "../../assets/mockups/onboarding_flow.png";
import logoGraziellas from "../../assets/logos/Graziella's Logo.jpg";
import logoLittleHoney from "../../assets/logos/Little Honey Logo.jpg";
import logoMeadowsweet from "../../assets/logos/Meadowsweet_Logo.webp";
import logoLaMorra from "../../assets/logos/LaMorra-Logo_small.webp";
import logoPunchBowl from "../../assets/logos/PunchBowl_logo.webp";
import logoMRU from "../../assets/logos/associations/MRU_Logo_Primary.png";
import logoEatDenver from "../../assets/logos/associations/EatDenver_Logo.webp";
import logoDBP from "../../assets/logos/associations/DowntownBrooklynDistrict.svg";
import logoNFBID from "../../assets/logos/associations/nfbid-logo-2022-400w.png";
import logoFAB from "../../assets/logos/associations/FAB.png";
import logoLES from "../../assets/logos/associations/LES_BID.webp";
import uploadMenuItems from "../../assets/features/Upload menu items.png";
import autoGeneratedLessons from "../../assets/features/Auto-generated lessons.png";
import managerDashboard from "../../assets/features/Manager Dashboard.png";
import PlaybookCaptureForm from "./PlaybookCaptureForm";
import {
  ALLERGEN_SAFETY_PILLAR_PATH,
  ALLERGEN_SAFETY_QA_GUIDE_PATH,
  MENU_TRAINING_CHECKLIST_PATH,
  MENU_TRAINING_PILLAR_PATH,
  MENU_TRAINING_TEMPLATE_PATH,
  SERVER_TRAINING_CHECKLIST_PATH,
  SERVER_TRAINING_PILLAR_PATH,
  shouldShowContentClusterPromos,
  WINE_TRAINING_PAIRING_GUIDE_PATH,
  WINE_TRAINING_PILLAR_PATH,
} from "../../lib/contentClusters";
import "../../styles/homepage-redesign.css";

const pillarCards = [
  {
    title: "Structured Lessons",
    body: "Short, repeatable modules built from your menu.",
    image: structuredLessons,
    alt: "Structured menu lesson preview showing short practice modules built from a restaurant menu",
  },
  {
    title: "Progress Tracking",
    body: "See completion and weak spots across staff.",
    image: clearProgressTracking,
    alt: "Manager progress dashboard preview showing completion and weak spots across staff",
  },
  {
    title: "Built-in Repetition",
    body: "Daily practice so knowledge sticks.",
    image: buildInRepitition,
    alt: "Daily repetition workflow preview for reinforcing restaurant menu knowledge",
  },
];

const problemSteps = [
  {
    title: "You train a new hire",
    body: "They shadow a few shifts and pick up the menu through repetition, memory, and whoever is on the floor that day.",
    tone: "default",
  },
  {
    title: "The information gets compressed",
    body: "They memorize the menu once, usually in a rush, without a system for review or recall.",
    tone: "default",
  },
  {
    title: "Details fade after the first weeks",
    body: "Ingredients, pairings, allergens, and modifiers start to blur as service gets busy.",
    tone: "impact",
  },
  {
    title: "Floor performance follows",
    body: "Confidence drops, upsells disappear, and order mistakes increase even when the team cares and works hard.",
    tone: "impact",
  },
  {
    title: "The root issue is the system",
    body: "Training is not structured, measurable, or reinforced over time, so retention becomes unpredictable.",
    tone: "root",
  },
];

const solutionChecks = [
  "Short, structured lessons",
  "Repetition over time",
  "Built-in recall",
  "Clear progress tracking",
];

const solutionSignals = ["Daily practice", "Manager visibility", "Consistent rollout"];

const howItWorksSteps = [
  {
    title: "Upload your menu",
    body: "Add menu items, allergens, dietary details, and photos once so every lesson starts from your actual menu.",
    image: uploadMenuItems,
    alt: "Menu setup screen for uploading dishes, ingredients, allergens, and dietary tags",
  },
  {
    title: "Auto-generate lessons",
    body: "Structured lessons are generated from your menu so staff can practice in short sessions instead of relying on one-time walkthroughs.",
    image: autoGeneratedLessons,
    alt: "Auto-generated lesson screen built from a restaurant menu",
  },
  {
    title: "Track and improve",
    body: "Managers can see progress, spot weak areas, and coach with real visibility instead of guessing who retained what.",
    image: managerDashboard,
    alt: "Manager dashboard screen showing training progress and weak knowledge areas",
  },
];

const roleBlocks = [
  {
    audience: "Restaurant Owners",
    title: "Get new hires floor-ready without rebuilding training every week.",
    intro: "For operators who want onboarding to feel consistent even when the floor is busy.",
    bullets: [
      "Get new hires productive faster.",
      "Standardize menu knowledge across shifts.",
      "Support better guest conversations without adding manual training work.",
    ],
  },
  {
    audience: "General Managers",
    title: "See where menu knowledge is slipping before it shows up in service.",
    intro: "For managers who need coaching visibility, not another binder or tasting sheet.",
    bullets: [
      "See who is keeping up with training.",
      "Catch weak spots before they show up on the floor.",
      "Replace binders and one-off tastings with a repeatable system.",
    ],
  },
  {
    audience: "Multi-Unit Operators",
    title: "Keep menu training aligned across every location, launch, and shift.",
    intro: "For leaders rolling out menu knowledge across multiple locations without chasing every team manually.",
    bullets: [
      "Keep menu training consistent across locations.",
      "Monitor progress without chasing every store manually.",
      "Roll out new menu knowledge with less operational drag.",
    ],
  },
];

const outcomes = ["Faster onboarding", "Fewer order errors", "More confident upselling"];

const logos = [
  { src: logoGraziellas, alt: "Graziella's restaurant logo" },
  { src: logoLittleHoney, alt: "Little Honey restaurant logo" },
  { src: logoMeadowsweet, alt: "Meadowsweet restaurant logo" },
  { src: logoLaMorra, alt: "La Morra restaurant logo" },
  { src: logoPunchBowl, alt: "Punch Bowl restaurant logo" },
];

const associations = [
  { src: logoMRU, alt: "Mass Restaurants United logo", imageClassName: "homepage-association-image-mru" },
  { src: logoEatDenver, alt: "Eat Denver logo" },
  { src: logoDBP, alt: "Downtown Brooklyn Partnership logo" },
  { src: logoNFBID, alt: "North Flatbush Business Improvement District logo" },
  { src: logoFAB, alt: "Fulton Business Alliance logo" },
  { src: logoLES, alt: "Lower East Side BID logo" },
];

const heroPhoneSizes = "(max-width: 640px) 304px, 372px";
const heroWineSrcSet = `${iphoneMockupWine760Webp} 760w, ${iphoneMockupWine960Webp} 960w`;
const heroFoodSrcSet = `${iphoneMockupFood760Webp} 760w, ${iphoneMockupFood960Webp} 960w`;

function SectionLabel({ children }) {
  return <span className="homepage-label-chip">{children}</span>;
}

function HeroSection() {
  const showContentClusterPromos = shouldShowContentClusterPromos();

  return (
    <section className="homepage-section homepage-hero-section">
      <div className="homepage-hero-bg" aria-hidden="true" />
      <div className="homepage-hero-glow homepage-hero-glow-left" aria-hidden="true" />
      <div className="homepage-hero-glow homepage-hero-glow-right" aria-hidden="true" />
      <div className="homepage-shell homepage-hero-grid">
        <div className="homepage-hero-copy">
          <SectionLabel>Menu Training Platform</SectionLabel>
          <h1 className="homepage-hero-title">Train staff on your menu in days, not weeks.</h1>
          <p className="homepage-hero-subhead">
            Structured lessons generated from your actual menu, with progress tracking for managers.
          </p>
          <div className="homepage-hero-media-wrap homepage-hero-media-wrap-mobile">
            <div className="homepage-device-stage">
              <div className="homepage-device-backdrop" aria-hidden="true" />
              <picture>
                <source type="image/webp" srcSet={heroWineSrcSet} sizes={heroPhoneSizes} />
                <img
                  src={iphoneMockupWine}
                  alt="Mobile wine training lesson on Speak Your Menu"
                  className="homepage-device-phone homepage-device-phone-back"
                  loading="eager"
                  decoding="async"
                  width={1024}
                  height={1536}
                />
              </picture>
              <picture>
                <source type="image/webp" srcSet={heroFoodSrcSet} sizes={heroPhoneSizes} />
                <img
                  src={iphoneMockupFood}
                  alt="Mobile food training lesson on Speak Your Menu"
                  className="homepage-device-phone homepage-device-phone-front"
                  loading="eager"
                  decoding="async"
                  width={1024}
                  height={1536}
                />
              </picture>
            </div>
          </div>
          <div className="homepage-hero-actions">
            <a
              href="https://app.speakyourmenu.com/signup"
              className="homepage-primary-cta"
              data-cta-name="start_free"
              data-cta-location="home_hero"
            >
              Start free
            </a>
            {showContentClusterPromos ? (
              <Link to={MENU_TRAINING_PILLAR_PATH} className="resource-secondary-link homepage-inline-resource-link">
                Read the menu training guide
              </Link>
            ) : null}
          </div>

          <div className="homepage-playbook-card homepage-playbook-card-hero">
            <p className="homepage-playbook-kicker">Free Training Guide</p>
            <h2 className="homepage-playbook-title">Free Menu Training Playbook</h2>
            <PlaybookCaptureForm source="homepage_playbook_hero" />
          </div>
        </div>

        <div className="homepage-hero-media-wrap homepage-hero-media-wrap-desktop">
          <div className="homepage-device-stage">
            <div className="homepage-device-backdrop" aria-hidden="true" />
            <picture>
              <source type="image/webp" srcSet={heroWineSrcSet} sizes={heroPhoneSizes} />
              <img
                src={iphoneMockupWine}
                alt="Mobile wine training lesson on Speak Your Menu"
                className="homepage-device-phone homepage-device-phone-back"
                loading="eager"
                decoding="async"
                width={1024}
                height={1536}
              />
            </picture>
            <picture>
              <source type="image/webp" srcSet={heroFoodSrcSet} sizes={heroPhoneSizes} />
              <img
                src={iphoneMockupFood}
                alt="Mobile food training lesson on Speak Your Menu"
                className="homepage-device-phone homepage-device-phone-front"
                loading="eager"
                decoding="async"
                width={1024}
                height={1536}
              />
            </picture>
          </div>
        </div>
      </div>
    </section>
  );
}

function PillarsSection() {
  return (
    <section className="homepage-section homepage-pillars-section">
      <div className="homepage-shell">
        <div className="homepage-pillar-grid">
          {pillarCards.map((card) => (
            <article className="homepage-surface-card homepage-pillar-card" key={card.title}>
              <div className="homepage-pillar-image-wrap">
                <img src={card.image} alt={card.alt} className="homepage-pillar-image" loading="lazy" decoding="async" />
              </div>
              <div className="homepage-pillar-divider" aria-hidden="true" />
              <div className="homepage-pillar-body">
                <h2 className="homepage-card-title">{card.title}</h2>
                <p className="homepage-card-copy">{card.body}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProblemSolutionSection() {
  return (
    <section className="homepage-section homepage-contrast-section homepage-section-alt">
      <div className="homepage-shell">
        <div className="homepage-contrast-header">
          <SectionLabel>WHY TRAINING BREAKS</SectionLabel>
          <h2 className="homepage-section-title">Better hospitality demands a better training system.</h2>
          <p className="homepage-section-body homepage-contrast-body">
            The issue usually is not effort. It is that menu knowledge gets taught once and then expected to hold up under pressure. The fix is a system that reinforces learning, reveals weak spots, and gives managers visibility.
          </p>
        </div>

        <div className="homepage-contrast-grid">
          <article className="homepage-surface-card homepage-contrast-card homepage-problem-card">
            <div className="homepage-contrast-card-head">
              <SectionLabel>WITHOUT STRUCTURE</SectionLabel>
              <h3 className="homepage-contrast-card-title">Training depends on memory and luck.</h3>
            </div>
            <div className="homepage-problem-journey" aria-label="What usually happens with manual menu training">
              {problemSteps.map((step, index) => (
                <div className={`homepage-problem-step homepage-problem-step-${step.tone}`} key={step.title}>
                  <div className="homepage-problem-marker" aria-hidden="true">
                    <span>{index + 1}</span>
                  </div>
                  <div className="homepage-problem-content">
                    <h4 className="homepage-problem-step-title">{step.title}</h4>
                    <p className="homepage-problem-line">{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </article>

          <article className="homepage-surface-card homepage-contrast-card homepage-solution-card">
            <div className="homepage-contrast-card-head homepage-contrast-card-head-solution">
              <SectionLabel>WITH SPEAK YOUR MENU</SectionLabel>
              <h3 className="homepage-contrast-card-title">Learning becomes structured, repeatable, and visible.</h3>
            </div>
            <div className="homepage-media-panel homepage-contrast-media-panel">
              <img src={onboardingFlow} alt="Speak Your Menu onboarding and progress view" className="homepage-media-panel-image" loading="lazy" decoding="async" />
            </div>
            <div className="homepage-surface-card homepage-check-card homepage-check-card-contrast">
              <ul className="homepage-check-list">
                {solutionChecks.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <p className="homepage-section-body homepage-solution-body">
              Speak Your Menu turns your actual menu into daily learning loops your team can complete in minutes. Managers can see progress, coach earlier, and keep knowledge consistent across every shift.
            </p>
            <div className="homepage-solution-signal-row" aria-label="Key solution outcomes">
              {solutionSignals.map((signal) => (
                <span className="homepage-solution-signal" key={signal}>
                  {signal}
                </span>
              ))}
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const sectionRef = React.useRef(null);
  const [hasAnimatedSteps, setHasAnimatedSteps] = React.useState(false);

  React.useEffect(() => {
    const sectionNode = sectionRef.current;

    if (!sectionNode || hasAnimatedSteps || typeof window === "undefined") {
      return undefined;
    }

    const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    if (reducedMotionQuery.matches) {
      return undefined;
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (!entry?.isIntersecting) {
          return;
        }

        setHasAnimatedSteps(true);
        observer.disconnect();
      },
      {
        threshold: 0.35,
        rootMargin: "0px 0px -10% 0px",
      }
    );

    observer.observe(sectionNode);

    return () => {
      observer.disconnect();
    };
  }, [hasAnimatedSteps]);

  return (
    <section
      ref={sectionRef}
      className={`homepage-section homepage-how-section homepage-section-alt${hasAnimatedSteps ? " homepage-how-section-animate" : ""}`}
    >
      <div className="homepage-shell">
        <SectionLabel>HOW IT WORKS</SectionLabel>
        <h2 className="homepage-section-title">How it works in 3 steps</h2>
        <div className="homepage-step-grid">
          {howItWorksSteps.map((step, index) => (
            <article className="homepage-surface-card homepage-step-card" key={step.title}>
              <div className="homepage-step-number" style={{ "--homepage-step-index": index }}>
                0{index + 1}
              </div>
              <div className="homepage-step-image-frame">
                <img src={step.image} alt={step.alt} className="homepage-step-image" loading="lazy" decoding="async" />
              </div>
              <h3 className="homepage-card-title">{step.title}</h3>
              <p className="homepage-card-copy">{step.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function RolesSection() {
  return (
    <section className="homepage-section homepage-roles-section">
      <div className="homepage-shell">
        <SectionLabel>WHO IT’S FOR</SectionLabel>
        <h2 className="homepage-section-title">For restaurant leaders turning menu knowledge into consistent floor performance.</h2>
        <p className="homepage-section-body homepage-role-section-body">
          Whether you are onboarding one new hire or standardizing training across multiple locations, the value is the same: less guessing, better retention, and more confidence on the floor.
        </p>
        <div className="homepage-role-grid">
          {roleBlocks.map((role) => (
            <article className="homepage-surface-card homepage-role-card" key={role.audience}>
              <div className="homepage-role-card-topline">
                <span className="homepage-role-card-audience">{role.audience}</span>
              </div>
              <h3 className="homepage-card-title homepage-role-card-title">{role.title}</h3>
              <p className="homepage-card-copy homepage-role-card-intro">{role.intro}</p>
              <div className="homepage-role-card-divider" aria-hidden="true" />
              <ul className="homepage-role-list">
                {role.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductProofSection() {
  const sectionRef = React.useRef(null);
  const imageRef = React.useRef(null);

  React.useEffect(() => {
    const sectionNode = sectionRef.current;
    const imageNode = imageRef.current;

    if (!sectionNode || !imageNode || typeof window === "undefined") {
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frameId = 0;

    const updateRotation = () => {
      frameId = 0;

      if (prefersReducedMotion.matches) {
        imageNode.style.setProperty("--homepage-proof-image-rotation", "-8deg");
        return;
      }

      const rect = sectionNode.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
      const progress = Math.min(1, Math.max(0, (viewportHeight - rect.top) / (viewportHeight + rect.height)));
      const rotation = -20 + progress * 40;

      imageNode.style.setProperty("--homepage-proof-image-rotation", `${rotation.toFixed(2)}deg`);
    };

    const requestUpdate = () => {
      if (!frameId) {
        frameId = window.requestAnimationFrame(updateRotation);
      }
    };

    requestUpdate();

    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
    prefersReducedMotion.addEventListener("change", requestUpdate);

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }

      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
      prefersReducedMotion.removeEventListener("change", requestUpdate);
    };
  }, []);

  return (
    <section className="homepage-section homepage-proof-section" ref={sectionRef}>
      <div className="homepage-shell homepage-proof-grid">
        <div className="homepage-proof-copy">
          <SectionLabel>PRODUCT PROOF</SectionLabel>
          <h2 className="homepage-section-title">Progress visibility for managers, daily practice for staff.</h2>
          <p className="homepage-section-body">
            Teams practice against the menu they actually serve, while managers get a clear dashboard for completion, weak spots, and coaching opportunities.
          </p>
          <div className="homepage-outcome-row">
            {outcomes.map((outcome) => (
              <div className="homepage-surface-card homepage-outcome-card" key={outcome}>
                <span className="homepage-outcome-card-label">{outcome}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="homepage-media-panel homepage-proof-panel">
          <img
            src={managerDashboard}
            alt="Manager dashboard showing training progress"
            className="homepage-media-panel-image"
            loading="lazy"
            decoding="async"
            ref={imageRef}
          />
        </div>
      </div>
    </section>
  );
}

function LogosSection() {
  if (!logos.length) return null;

  return (
    <section className="homepage-section homepage-section-alt homepage-logos-section">
      <div className="homepage-shell homepage-logos-shell">
        <SectionLabel>Trusted By</SectionLabel>
        <h2 className="homepage-section-title homepage-logos-heading">
          Restaurants trust Speak Your Menu to train their teams.
        </h2>
        <div className="homepage-logo-strip" aria-label="Restaurants that use Speak Your Menu">
          {logos.map((logo) => (
            <div className="homepage-logo-tile" key={logo.alt}>
              <img src={logo.src} alt={logo.alt} className="homepage-logo-image" loading="lazy" decoding="async" />
            </div>
          ))}
        </div>
        <p className="homepage-logos-subtext">
          Independent restaurants and multi-unit operators use Speak Your Menu to onboard faster, reduce errors, and build consistent menu knowledge.
        </p>
      </div>
    </section>
  );
}

function FinalCtaSection() {
  const location = useLocation();
  const sectionRef = useRef(null);
  const [isSpotlighted, setIsSpotlighted] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    setIsSpotlighted(false);

    if (location.hash !== "#homepage-playbook") {
      return undefined;
    }

    let spotlightTimeoutId;
    let focusTimeoutId;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;

        setIsSpotlighted(true);
        observer.disconnect();

        const emailInput = section.querySelector('input[type="email"]');
        if (emailInput instanceof HTMLInputElement) {
          focusTimeoutId = window.setTimeout(() => {
            emailInput.focus({ preventScroll: true });
            emailInput.select();
          }, 420);
        }

        spotlightTimeoutId = window.setTimeout(() => {
          setIsSpotlighted(false);
        }, 2600);
      },
      {
        threshold: 0.45,
      }
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
      if (spotlightTimeoutId) {
        window.clearTimeout(spotlightTimeoutId);
      }
      if (focusTimeoutId) {
        window.clearTimeout(focusTimeoutId);
      }
    };
  }, [location.hash]);

  return (
    <section
      ref={sectionRef}
      className={`homepage-section homepage-final-cta-section${isSpotlighted ? " homepage-final-cta-section-spotlight" : ""}`}
      id="homepage-playbook"
    >
      <div className="homepage-shell homepage-final-cta-shell">
        <div>
          <SectionLabel>FREE TRAINING GUIDE</SectionLabel>
          <h2 className="homepage-section-title">Get the free menu training playbook</h2>
          <p className="homepage-section-body homepage-final-cta-body">
            A practical guide you can use in your restaurant to sharpen onboarding, improve menu knowledge, and build a training system your staff can actually retain.
          </p>
        </div>
        <div className="homepage-final-cta-form-wrap">
          <PlaybookCaptureForm source="homepage_playbook_footer" compact />
        </div>
      </div>
    </section>
  );
}

function ResourceClusterSection() {
  return (
    <section className="homepage-section homepage-section-alt homepage-resource-cluster-section">
      <div className="homepage-shell resource-grid-two resource-grid-two-stackable">
        <article className="homepage-surface-card resource-card">
          <SectionLabel>Learning Hub</SectionLabel>
          <h2 className="homepage-section-title resource-section-title">Start with the menu training system, then use the checklist to roll it out.</h2>
          <p className="homepage-section-body resource-section-body">
            If you are evaluating how to train restaurant staff on the menu, the strongest starting point is a system that combines accurate menu data, repeated practice, and manager visibility.
          </p>
          <div className="resource-inline-actions">
            <Link to={MENU_TRAINING_PILLAR_PATH} className="homepage-primary-cta">Read the guide</Link>
            <Link to={MENU_TRAINING_TEMPLATE_PATH} className="resource-secondary-link">Get the template</Link>
            <Link to={MENU_TRAINING_CHECKLIST_PATH} className="resource-secondary-link">Open the checklist</Link>
          </div>
        </article>
        <article className="homepage-surface-card resource-card resource-card-contrast">
          <SectionLabel>Why this matters</SectionLabel>
          <ul className="resource-bullet-list resource-bullet-list-tight">
            <li>Faster onboarding for new hires who need menu confidence quickly.</li>
            <li>Stronger service consistency when shifts and trainers vary.</li>
            <li>More reliable guest answers on ingredients, allergens, and pairings.</li>
          </ul>
        </article>
        <article className="homepage-surface-card resource-card">
          <SectionLabel>Server Training Hub</SectionLabel>
          <h2 className="homepage-section-title resource-section-title">Use the server-training guide when the onboarding system is the real bottleneck.</h2>
          <p className="homepage-section-body resource-section-body">
            If new hires still depend too heavily on shadow shifts, use the server-training guide and onboarding checklist to tighten first-week expectations and reinforcement.
          </p>
          <div className="resource-inline-actions">
            <Link to={SERVER_TRAINING_PILLAR_PATH} className="homepage-primary-cta">Read the guide</Link>
            <Link to={SERVER_TRAINING_CHECKLIST_PATH} className="resource-secondary-link">Open onboarding checklist</Link>
          </div>
        </article>
        <article className="homepage-surface-card resource-card resource-card-accent">
          <SectionLabel>Wine Training Hub</SectionLabel>
          <h2 className="homepage-section-title resource-section-title">Use the wine-training guide when beverage confidence and sales are the real gap.</h2>
          <p className="homepage-section-body resource-section-body">
            If staff know the list exists but still cannot recommend bottles or pairings clearly, start with the wine-training guide and pairing guide.
          </p>
          <div className="resource-inline-actions">
            <Link to={WINE_TRAINING_PILLAR_PATH} className="homepage-primary-cta">Read the guide</Link>
            <Link to={WINE_TRAINING_PAIRING_GUIDE_PATH} className="resource-secondary-link">Open pairing guide</Link>
          </div>
        </article>
        <article className="homepage-surface-card resource-card">
          <SectionLabel>Allergen Safety Hub</SectionLabel>
          <h2 className="homepage-section-title resource-section-title">Use the allergen-safety guide when answer accuracy cannot depend on memory alone.</h2>
          <p className="homepage-section-body resource-section-body">
            If staff hesitate on allergens, substitutions, or dietary questions, use the allergen-safety guide and Q&A guide to tighten service language and escalation habits.
          </p>
          <div className="resource-inline-actions">
            <Link to={ALLERGEN_SAFETY_PILLAR_PATH} className="homepage-primary-cta">Read the guide</Link>
            <Link to={ALLERGEN_SAFETY_QA_GUIDE_PATH} className="resource-secondary-link">Open Q&amp;A guide</Link>
          </div>
        </article>
      </div>
    </section>
  );
}

function AssociationsSection() {
  if (!associations.length) return null;

  return (
    <section className="homepage-section homepage-associations-section homepage-associations-section-branded">
      <div className="homepage-shell homepage-associations-shell">
        <SectionLabel>Partnerships</SectionLabel>
        <h2 className="homepage-section-title homepage-associations-heading">
          We work alongside the associations that represent the restaurant industry.
        </h2>
        <div className="homepage-association-strip" aria-label="Restaurant associations we partner with">
          {associations.map((assoc) => (
            <div className="homepage-association-tile" key={assoc.alt}>
              <img
                src={assoc.src}
                alt={assoc.alt}
                className={["homepage-association-image", assoc.imageClassName].filter(Boolean).join(" ")}
                loading="lazy"
                decoding="async"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function HomepageRedesign() {
  const showLogosSection = true;
  const showContentClusterPromos = shouldShowContentClusterPromos();

  return (
    <div className="homepage-redesign">
      <HeroSection />
      <PillarsSection />
      <AssociationsSection />
      <ProblemSolutionSection />
      <HowItWorksSection />
      <RolesSection />
      <ProductProofSection />
      {showContentClusterPromos ? <ResourceClusterSection /> : null}
      {showLogosSection ? <LogosSection /> : null}
      <FinalCtaSection />
    </div>
  );
}